<template>
  <div class="user-management-container">
    <div class="header-section">
      <h2>User Management</h2>
      <button @click="openUserTypeModal" class="btn-add-usertype">
        <i class="fas fa-plus"></i> Add New UserType
      </button>
    </div>
    
    <div class="management-content">
      <!-- Sidebar - User Roles -->
      <div class="sidebar">
        <div v-if="fetchedUserList.length === 0" class="sidebar-empty">
          Loading users...
        </div>
        <div 
          v-for="user in fetchedUserList" 
          :key="user.roleid"
          class="role-item"
          :class="{ active: selectedUserId === user.roleid }"
          @click="selectUser(user)"
        >
          <span class="role-name">{{ user.role_description || user.name || user.title || user.label }}</span>
          <button 
            @click.stop="openUserTypeModal(user)"
            class="role-edit-btn"
            title="Edit UserType"
          >
            <i class="fas fa-edit"></i>
          </button>
        </div>
      </div>

      <!-- Main Content - Menu Structure -->
      <div class="main-content">
        <div v-if="selectedUser" class="menu-structure">
          <h3>{{ selectedUser.role_description || selectedUser.name }} - Menu Permissions</h3>
          
          <div v-if="filteredMenuList.length === 0" class="no-menus">
            <p>No menus available for this role</p>
          </div>
          <div v-else class="menu-tree">
            <!-- Recursive menu component -->
            <div 
              v-for="menu in filteredMenuList" 
              :key="menu.menuid"
              class="menu-item"
            >
              <div class="menu-header" :class="{ 'has-permissions': !menu.children || menu.children.length === 0 }">
                <div 
                  v-if="menu.children && menu.children.length > 0"
                  @click="toggleMenuExpand(menu)"
                  class="expand-toggle"
                  :title="isMenuExpanded(menu) ? 'Collapse' : 'Expand'"
                >
                  <i :class="isMenuExpanded(menu) ? 'fas fa-minus' : 'fas fa-plus'"></i>
                </div>
                <span v-else class="expand-placeholder"></span>
                
                <input 
                  type="checkbox" 
                  :checked="menu.children && menu.children.length > 0 ? isParentMenuSelected(menu) : (menu.viewaction === 1)"
                  @change="menu.children && menu.children.length > 0 ? toggleParentMenu(menu) : toggleLeafMenu(menu)"
                  class="menu-checkbox"
                >
                
                <i v-if="menu.menu_icon" :class="menu.menu_icon" class="menu-icon"></i>
                <span class="menu-name">{{ menu.menu_name }}</span>
                
                <!-- Show permissions for parent menus with no children -->
                <div v-if="!menu.children || menu.children.length === 0" class="permissions-inline">
                  <label class="permission-label">
                    <input type="checkbox" :checked="menu.viewaction === 1" @change="togglePermission(menu, 'viewaction')">
                    <span>View</span>
                  </label>
                  <label class="permission-label">
                    <input type="checkbox" :checked="menu.editaction === 1" @change="togglePermission(menu, 'editaction')">
                    <span>Edit</span>
                  </label>
                  <label class="permission-label">
                    <input type="checkbox" :checked="menu.deleteaction === 1" @change="togglePermission(menu, 'deleteaction')">
                    <span>Delete</span>
                  </label>
                </div>
              </div>

              <!-- Render children -->
              <div v-if="isMenuExpanded(menu) && menu.children && menu.children.length > 0" class="menu-children">
                <div 
                  v-for="child in menu.children" 
                  :key="child.menuid"
                  class="menu-item child-menu-row"
                >
                  <div class="child-menu-header">
                    <input 
                      type="checkbox" 
                      :checked="isMenuSelected(child)"
                      @change="toggleChildMenu(child)"
                      class="menu-checkbox"
                    >
                    <i v-if="child.menu_icon" :class="child.menu_icon" class="menu-icon"></i>
                    <span class="menu-name">{{ child.menu_name }}</span>
                    
                    <div class="permissions-inline">
                      <label class="permission-label">
                        <input type="checkbox" :checked="child.viewaction === 1" @change="togglePermission(child, 'viewaction')" :disabled="!isMenuSelected(child)">
                        <span>View</span>
                      </label>
                      <label class="permission-label">
                        <input type="checkbox" :checked="child.editaction === 1" @change="togglePermission(child, 'editaction')" :disabled="!isMenuSelected(child)">
                        <span>Edit</span>
                      </label>
                      <label class="permission-label">
                        <input type="checkbox" :checked="child.deleteaction === 1" @change="togglePermission(child, 'deleteaction')" :disabled="!isMenuSelected(child)">
                        <span>Delete</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="no-selection">
          <p>Select a user role to manage menu permissions</p>
        </div>

        <!-- Action Buttons -->
        <div class="action-buttons">
          <button @click="savePermissions" class="btn-save">Save</button>
          <button @click="cancelChanges" class="btn-cancel">Cancel</button>
        </div>
      </div>
    </div>

    <!-- UserType Modal -->
    <div v-if="showUserTypeModal" class="modal-overlay" @click.self="closeUserTypeModal">
      <div class="modal-content-usertype">
        <div class="modal-header">
          <h3>{{ editingUserType ? 'Edit UserType' : 'Add New UserType' }}</h3>
          <button class="close-btn" @click="closeUserTypeModal">&times;</button>
        </div>

        <div class="modal-body">
          <div class="form-group">
            <label for="role-description">Role Description <span class="required">*</span></label>
            <input
              id="role-description"
              v-model="userTypeFormData.role_description"
              type="text"
              class="form-input"
              placeholder="Enter role description"
              @keyup.enter="saveUserType"
            />
            <span v-if="formErrors.role_description" class="error-text">
              {{ formErrors.role_description }}
            </span>
          </div>
        </div>

        <div class="modal-footer">
          <button @click="saveUserType" class="btn-save-usertype">
            <i class="fas fa-save"></i> Save
          </button>
          <button @click="closeUserTypeModal" class="btn-cancel-usertype">
            <i class="fas fa-times"></i> Cancel
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';

export default {
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      fetchedUserList: [],
      fetchedMenuList: [],
      selectedUserId: 1,
      selectedUser: null,
      expandedMenus: [],
      selectedMenus: {},
      permissions: {},
      showUserTypeModal: false,
      editingUserType: null,
      userTypeFormData: {
        role_description: '',
      },
      formErrors: {},
    };
  },
  computed: {
    filteredMenuList() {
      // Return the hierarchical tree
      return this.fetchedMenuList;
    },
  },
  mounted() {
    this.fetchUserType().then(() => {
      // After user types are loaded, select the first one (Administrator by default)
      if (this.fetchedUserList.length > 0) {
        this.selectUser(this.fetchedUserList[0]);
      }
    });
  },
  methods: {
    async fetchUserType() {
      try {
        const token = localStorage.getItem('access_token');
        const config = {
          headers: {
            'accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        };
          const response = await axios.get('/usertype', config);
        
        // Direct array of role objects
        if (Array.isArray(response.data) && response.data.length > 0) {
          if (response.data[0].roleid !== undefined) {
            // Array of role objects with roleid and role_description
            this.fetchedUserList = response.data;
          } else if (response.data[0] === 1 && response.data[2]) {
            // Format: [1, "message", [...]]
            this.fetchedUserList = response.data[2];
          }
        } else if (response.data.data) {
          this.fetchedUserList = response.data.data;
        }
        return Promise.resolve();
      } catch (error) {
        console.error('Error fetching user type:', error);
        return Promise.reject(error);
      }
    },
    async fetchMenuByRole() {
      try {
        const token = localStorage.getItem('access_token');
        const config = {
          headers: {
            'accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        };
        const response = await axios.get('/menulist_by_role?role=' + this.selectedUserId, config);        
        let menuList = [];
        // Format: [1, "Data Found", [...menus]]
        if (response.data[0] === 1 && response.data[2]) {
          menuList = response.data[2];
        } else if (Array.isArray(response.data)) {
          menuList = response.data;
        } else if (response.data.data) {
          menuList = response.data.data;
        }
        
        // Build hierarchical tree
        this.fetchedMenuList = this.buildMenuTree(menuList);
      } catch (error) {
        console.error('Error fetching menu list by role:', error);
      }
    },
    buildMenuTree(flatMenus) {
      // Build hierarchical tree from flat list
      const menuMap = {};
      const tree = [];
      
      // Create map of menus by menuid
      flatMenus.forEach(menu => {
        menuMap[menu.menuid] = { ...menu, children: [] };
      });
      
      // Build tree structure
      flatMenus.forEach(menu => {
        if (menu.parent_id === 0) {
          // Top-level menu
          tree.push(menuMap[menu.menuid]);
        } else if (menuMap[menu.parent_id]) {
          // Add as child to parent
          menuMap[menu.parent_id].children.push(menuMap[menu.menuid]);
        }
      });
      
      // Sort by order_id
      tree.sort((a, b) => a.order_id - b.order_id);
      
      return tree;
    },
    selectUser(user) {
      this.selectedUserId = user.roleid;
      this.selectedUser = user;
      this.fetchMenuByRole();
    },
    toggleMenuExpand(menu) {
      const index = this.expandedMenus.findIndex(m => m.menuid === menu.menuid);
      if (index > -1) {
        this.expandedMenus.splice(index, 1);
      } else {
        this.expandedMenus.push(menu);
      }
    },
    isMenuExpanded(menu) {
      return this.expandedMenus.some(m => m.menuid === menu.menuid);
    },
    toggleMenu(menu) {
      if (!this.selectedMenus[this.selectedUserId]) {
        this.selectedMenus[this.selectedUserId] = [];
      }
      const index = this.selectedMenus[this.selectedUserId].findIndex(m => m.menuid === menu.menuid);
      if (index > -1) {
        this.selectedMenus[this.selectedUserId].splice(index, 1);
      } else {
        this.selectedMenus[this.selectedUserId].push(menu);
      }
    },
    isParentMenuSelected(menu) {
      // Check if parent has any child with view permission
      if (menu.children && menu.children.length > 0) {
        return menu.children.some(child => child.viewaction === 1);
      }
      
      if (!this.selectedMenus[this.selectedUserId]) {
        return false;
      }
      return this.selectedMenus[this.selectedUserId].some(m => m.menuid === menu.menuid);
    },
    toggleParentMenu(menu) {
      // Toggle parent selection
      const isCurrentlySelected = this.isParentMenuSelected(menu);
      
      if (isCurrentlySelected) {
        // Uncheck parent and all children
        if (this.selectedMenus[this.selectedUserId]) {
          const index = this.selectedMenus[this.selectedUserId].findIndex(m => m.menuid === menu.menuid);
          if (index > -1) {
            this.selectedMenus[this.selectedUserId].splice(index, 1);
          }
        }
        // Reset all children permissions
        if (menu.children && menu.children.length > 0) {
          menu.children.forEach(child => {
            child.viewaction = 0;
            child.editaction = 0;
            child.deleteaction = 0;
            // Remove from selected list
            if (this.selectedMenus[this.selectedUserId]) {
              const childIndex = this.selectedMenus[this.selectedUserId].findIndex(m => m.menuid === child.menuid);
              if (childIndex > -1) {
                this.selectedMenus[this.selectedUserId].splice(childIndex, 1);
              }
            }
          });
        }
      } else {
        // Check parent and all children with view permission
        if (!this.selectedMenus[this.selectedUserId]) {
          this.selectedMenus[this.selectedUserId] = [];
        }
        if (!this.selectedMenus[this.selectedUserId].some(m => m.menuid === menu.menuid)) {
          this.selectedMenus[this.selectedUserId].push(menu);
        }
        // Auto-check all children with view permission
        if (menu.children && menu.children.length > 0) {
          menu.children.forEach(child => {
            if (child.viewaction === 1) {
              if (!this.selectedMenus[this.selectedUserId].some(m => m.menuid === child.menuid)) {
                this.selectedMenus[this.selectedUserId].push(child);
              }
            }
          });
        }
      }
    },
    isMenuSelected(menu) {
      // Auto-select if child has view permission
      if (menu.viewaction === 1) {
        return true;
      }
      
      if (!this.selectedMenus[this.selectedUserId]) {
        return false;
      }
      return this.selectedMenus[this.selectedUserId].some(m => m.menuid === menu.menuid);
    },
    toggleChildMenu(child) {
      // Toggle selection
      const isCurrentlySelected = this.isMenuSelected(child);
      
      if (isCurrentlySelected) {
        // Uncheck: remove from selected and reset all permissions
        if (this.selectedMenus[this.selectedUserId]) {
          const index = this.selectedMenus[this.selectedUserId].findIndex(m => m.menuid === child.menuid);
          if (index > -1) {
            this.selectedMenus[this.selectedUserId].splice(index, 1);
          }
        }
        // Reset all permissions
        child.viewaction = 0;
        child.editaction = 0;
        child.deleteaction = 0;
        // Update parent selection
        this.updateParentSelection();
      } else {
        // Check: add to selected and auto-check view
        if (!this.selectedMenus[this.selectedUserId]) {
          this.selectedMenus[this.selectedUserId] = [];
        }
        this.selectedMenus[this.selectedUserId].push(child);
        // Auto-check view
        child.viewaction = 1;
        
        // Auto-select parent if any child is checked
        this.updateParentSelection();
      }
    },
    toggleLeafMenu(menu) {
      // Toggle selection for parent menus with no children (leaf menus)
      const isCurrentlySelected = menu.viewaction === 1;
      
      if (isCurrentlySelected) {
        // Uncheck: reset all permissions
        menu.viewaction = 0;
        menu.editaction = 0;
        menu.deleteaction = 0;
        // Remove from selected
        if (this.selectedMenus[this.selectedUserId]) {
          const index = this.selectedMenus[this.selectedUserId].findIndex(m => m.menuid === menu.menuid);
          if (index > -1) {
            this.selectedMenus[this.selectedUserId].splice(index, 1);
          }
        }
      } else {
        // Check: add to selected and auto-check view
        if (!this.selectedMenus[this.selectedUserId]) {
          this.selectedMenus[this.selectedUserId] = [];
        }
        this.selectedMenus[this.selectedUserId].push(menu);
        // Auto-check view
        menu.viewaction = 1;
      }
    },
    togglePermission(menu, permissionField) {
      // Toggle permission: 0 -> 1 or 1 -> 0
      menu[permissionField] = menu[permissionField] === 1 ? 0 : 1;
      
      // Handle view permission changes
      if (permissionField === 'viewaction') {
        if (menu.viewaction === 1) {
          // View enabled: auto-select child
          if (!this.selectedMenus[this.selectedUserId]) {
            this.selectedMenus[this.selectedUserId] = [];
          }
          if (!this.selectedMenus[this.selectedUserId].some(m => m.menuid === menu.menuid)) {
            this.selectedMenus[this.selectedUserId].push(menu);
          }
        } else {
          // View disabled: remove from selected and reset other permissions
          if (this.selectedMenus[this.selectedUserId]) {
            const index = this.selectedMenus[this.selectedUserId].findIndex(m => m.menuid === menu.menuid);
            if (index > -1) {
              this.selectedMenus[this.selectedUserId].splice(index, 1);
            }
          }
          menu.editaction = 0;
          menu.deleteaction = 0;
        }
        
        // Auto-select/deselect parent
        this.updateParentSelection();
      }
    },
    updateParentSelection() {
      // Find and select parent menu if any child has view permission
      this.fetchedMenuList.forEach(parentMenu => {
        if (parentMenu.children && parentMenu.children.length > 0) {
          const hasViewPermission = parentMenu.children.some(child => child.viewaction === 1);
          
          if (hasViewPermission) {
            // Auto-select parent
            if (!this.selectedMenus[this.selectedUserId]) {
              this.selectedMenus[this.selectedUserId] = [];
            }
            if (!this.selectedMenus[this.selectedUserId].some(m => m.menuid === parentMenu.menuid)) {
              this.selectedMenus[this.selectedUserId].push(parentMenu);
            }
            // Auto-expand parent
            if (!this.expandedMenus.some(m => m.menuid === parentMenu.menuid)) {
              this.expandedMenus.push(parentMenu);
            }
          } else {
            // Remove parent from selected if no child has view permission
            if (this.selectedMenus[this.selectedUserId]) {
              const index = this.selectedMenus[this.selectedUserId].findIndex(m => m.menuid === parentMenu.menuid);
              if (index > -1) {
                this.selectedMenus[this.selectedUserId].splice(index, 1);
              }
            }
          }
        }
      });
    },
    hasPermission(menu, permission) {
      if (!this.permissions[this.selectedUserId] || !this.permissions[this.selectedUserId][menu.menuid]) {
        return false;
      }
      return this.permissions[this.selectedUserId][menu.menuid].includes(permission);
    },
    async savePermissions() {
      const permissions = [];
      
      // Collect all menu permissions
      const collectPermissions = (menus) => {
        menus.forEach(menu => {
          // Determine if checkbox is checked using the same logic as template
          let isCheckboxChecked = false;
          if (menu.children && menu.children.length > 0) {
            // Parent menu: use isParentMenuSelected logic
            isCheckboxChecked = this.isParentMenuSelected(menu);
          } else {
            // Leaf menu: checkbox is checked if viewaction === 1
            isCheckboxChecked = menu.viewaction === 1;
          }
          
          // Set viewaction based on checkbox state
          let viewaction = isCheckboxChecked ? 1 : 0;
          
          
          permissions.push({
            menuid: menu.menuid,
            viewaction: viewaction,
            editaction: menu.editaction,
            deleteaction: menu.deleteaction,
          });
          
          if (menu.children && menu.children.length > 0) {
            collectPermissions(menu.children);
          }
        });
      };
      
      collectPermissions(this.fetchedMenuList);
            
      try {
        const token = localStorage.getItem('access_token');
        const formData = new FormData();
        formData.append('transaction', JSON.stringify(permissions));
        
        const config = {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        };
        
        const response = await axios.post(`/save_usermenuaction/${this.selectedUserId}`, formData, config);
        
        if (response.data[0] === 1 || response.status === 200) {
          this.toast.success('Permissions saved successfully for ' + this.selectedUser.role_description);
        } else {
          this.toast.error('Error: ' + (response.data[1] || 'Failed to save permissions'));
        }
      } catch (error) {
        console.error('Error saving permissions:', error);
        this.toast.error('Error saving permissions: ' + (error.response?.data?.[1] || error.message));
      }
    },
    cancelChanges() {
      // Reset to fetch fresh data
      this.fetchMenuByRole();
      this.selectedMenus = {};
      this.permissions = {};
      this.expandedMenus = [];
      this.selectedUserId = null;
      this.selectedUser = null;
    },
    openUserTypeModal(userType = null) {
      this.editingUserType = userType;
      if (userType) {
        this.userTypeFormData = { ...userType };
      } else {
        this.userTypeFormData = { role_description: '' };
      }
      this.formErrors = {};
      this.showUserTypeModal = true;
    },
    closeUserTypeModal() {
      this.showUserTypeModal = false;
      this.editingUserType = null;
      this.userTypeFormData = { role_description: '' };
      this.formErrors = {};
    },
    validateUserTypeForm() {
      this.formErrors = {};
      
      if (!this.userTypeFormData.role_description || this.userTypeFormData.role_description.trim() === '') {
        this.formErrors.role_description = 'Role Description is required';
      }
      
      return Object.keys(this.formErrors).length === 0;
    },
    async saveUserType() {
      if (!this.validateUserTypeForm()) {
        return;
      }
      
      try {
        const token = localStorage.getItem('access_token');
        const formData = new FormData();
        formData.append('transaction', JSON.stringify(this.userTypeFormData));
        
        const config = {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        };
        
        const endpoint = (this.editingUserType && this.editingUserType.roleid) 
          ? `/update_usertype/${this.editingUserType.roleid}`
          : '/create_usertype';
        
        const response = await axios.post(endpoint, formData, config);
        
        if (response.data[0] === 1 || response.status === 200) {
          this.toast.success((this.editingUserType ? 'Updated' : 'Created') + ' UserType successfully');
          this.closeUserTypeModal();
          this.fetchUserType(); // Refresh the user list
        } else {
          this.toast.error('Error: ' + (response.data[1] || 'Failed to save UserType'));
        }
      } catch (error) {
        console.error('Error saving UserType:', error);
        this.toast.error('Error saving UserType: ' + (error.response?.data?.[1] || error.message));
      }
    },
  },
};  
</script>
<style scoped>
.user-management-container {
  padding: 20px;
  background-color: #f5f5f5;
  min-height: 100vh;
}

h2 {
  margin-bottom: 20px;
  color: #333;
}

.header-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.btn-add-usertype {
  background-color: #4CAF50;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background-color 0.3s ease;
}

.btn-add-usertype:hover {
  background-color: #45a049;
}

.management-content {
  display: flex;
  gap: 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.sidebar {
  width: 220px;
  background: #f9f9f9;
  border-right: 1px solid #e0e0e0;
  padding: 15px 0;
  max-height: 600px;
  overflow-y: auto;
}

.sidebar-empty {
  padding: 20px 15px;
  text-align: center;
  color: #999;
  font-size: 12px;
}

.role-item {
  padding: 12px 15px;
  cursor: pointer;
  border-left: 3px solid transparent;
  transition: all 0.3s ease;
  color: #333;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.role-item:hover {
  background-color: #e8f4f8;
}

.role-item:hover .role-edit-btn {
  opacity: 1;
}

.role-item.active {
  background-color: #2196F3;
  color: white;
  border-left-color: #1976D2;
}

.role-name {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.role-edit-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: #2196F3;
  font-size: 14px;
  padding: 4px 8px;
  opacity: 0;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.role-item.active .role-edit-btn {
  color: white;
}

.role-edit-btn:hover {
  transform: scale(1.2);
}

.main-content {
  flex: 1;
  padding: 20px;
  display: flex;
  flex-direction: column;
}

.menu-structure {
  flex: 1;
  margin-bottom: 20px;
}

.menu-structure h3 {
  margin-bottom: 15px;
  color: #2196F3;
  font-size: 16px;
}

.menu-tree {
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
}

.menu-item {
  border-bottom: 1px solid #f0f0f0;
  padding: 10px;
  transition: background-color 0.2s;
}

.menu-item:last-child {
  border-bottom: none;
}

.menu-item:hover {
  background-color: #fafafa;
}

.menu-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
  color: #333;
  padding: 10px;
  cursor: pointer;
  user-select: none;
}

.menu-header.has-permissions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.expand-checkbox,
.menu-checkbox {
  cursor: pointer;
  width: 18px;
  height: 18px;
}

.expand-placeholder {
  width: 18px;
  height: 18px;
  display: inline-block;
}

.menu-icon {
  width: 18px;
  margin-right: 8px;
  color: #666;
  font-size: 14px;
  min-width: 18px;
}

.child-menu {
  margin-left: 30px;
  border-left: 1px solid #e0e0e0;
  padding-left: 10px;
}

.menu-children {
  padding-left: 10px;
}

.child-menu-row {
  margin: 0;
  padding: 0;
}

.child-menu-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px;
  border-left: 1px solid #e0e0e0;
  margin-left: 20px;
  padding-left: 15px;
}

.permissions-inline {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-left: auto;
  padding-left: 20px;
  flex-shrink: 0;
}

.permission-label {
  display: flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
  font-size: 13px;
  color: #666;
}

.permission-label input[type="checkbox"] {
  cursor: pointer;
  width: 16px;
  height: 16px;
}

.permission-label input[type="checkbox"]:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.menu-name {
  flex: 1;
  user-select: none;
}

.menu-permissions {
  display: flex;
  gap: 15px;
  margin-top: 8px;
  padding-left: 26px;
  font-size: 13px;
  color: #666;
}

.menu-permissions label {
  display: flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  user-select: none;
}

.menu-permissions input[type="checkbox"] {
  cursor: pointer;
  width: 16px;
  height: 16px;
}

.no-selection {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  color: #999;
  font-size: 14px;
}

.no-menus {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  color: #999;
  font-size: 14px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
}

.action-buttons {
  display: flex;
  gap: 10px;
  justify-content: center;
  padding-top: 15px;
  border-top: 1px solid #e0e0e0;
}

.btn-save,
.btn-cancel {
  padding: 10px 30px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.btn-save {
  background-color: #2196F3;
  color: white;
}

.btn-save:hover {
  background-color: #1976D2;
  box-shadow: 0 2px 8px rgba(33, 150, 243, 0.3);
}

.btn-cancel {
  background-color: #f0f0f0;
  color: #333;
  border: 1px solid #ddd;
}

.btn-cancel:hover {
  background-color: #e0e0e0;
}

/* UserType Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content-usertype {
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #e0e0e0;
}

.modal-header h3 {
  margin: 0;
  color: #2196F3;
  font-size: 18px;
}

.close-btn {
  background: none;
  border: none;
  font-size: 28px;
  cursor: pointer;
  color: #666;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.3s ease;
}

.close-btn:hover {
  color: #333;
}

.modal-body {
  padding: 20px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  color: #333;
  font-weight: 500;
  font-size: 14px;
}

.required {
  color: #f44336;
}

.form-input {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;
  transition: border-color 0.3s ease;
}

.form-input:focus {
  outline: none;
  border-color: #2196F3;
  box-shadow: 0 0 5px rgba(33, 150, 243, 0.3);
}

.error-text {
  color: #f44336;
  font-size: 12px;
  margin-top: 5px;
  display: block;
}

.modal-footer {
  display: flex;
  gap: 10px;
  justify-content: center;
  padding: 15px 20px;
  border-top: 1px solid #e0e0e0;
  background-color: #f9f9f9;
}

.btn-save-usertype,
.btn-cancel-usertype {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
}

.btn-save-usertype {
  background-color: #2196F3;
  color: white;
}

.btn-save-usertype:hover {
  background-color: #1976D2;
}

.btn-cancel-usertype {
  background-color: #f0f0f0;
  color: #333;
  border: 1px solid #ddd;
}

.btn-cancel-usertype:hover {
  background-color: #e0e0e0;
}
</style>